import { MessageSquarePlus, Trash2, History, Coins } from "lucide-react";
import type { AiSearchConversationSummary } from "@/services/aiSearch.service";

function formatRelativeTime(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

interface ChatHistorySidebarProps {
  chats: AiSearchConversationSummary[];
  activeChatId: string | null;
  credits: number;
  maxCredits: number;
  hasCredits: boolean;
  planName?: string;
  chatsLoading?: boolean;
  creditsLoading?: boolean;
  onNewChat: () => void;
  onSelectChat: (chatId: string) => void;
  onDeleteChat: (chatId: string) => void;
  disabled?: boolean;
  className?: string;
}

function ChatListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-1" aria-busy="true" aria-label="Loading chat history">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="px-3 py-2.5 rounded-lg animate-pulse">
          <div
            className="h-3.5 bg-muted rounded"
            style={{ width: `${70 - (i % 3) * 12}%` }}
          />
          <div className="mt-2 h-2.5 w-20 bg-muted/80 rounded" />
        </div>
      ))}
    </div>
  );
}

function CreditsBarSkeleton() {
  return (
    <div
      className="p-3 border-t border-border bg-card/80 shrink-0 animate-pulse"
      aria-busy="true"
      aria-label="Loading credits"
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <div className="w-3.5 h-3.5 rounded bg-muted" />
          <div className="h-3 w-20 bg-muted rounded" />
        </div>
        <div className="h-3 w-10 bg-muted rounded" />
      </div>
      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
        <div className="h-full w-1/3 rounded-full bg-muted" />
      </div>
      <div className="mt-1.5 h-2.5 w-4/5 max-w-[180px] bg-muted/80 rounded" />
    </div>
  );
}

function CreditsBar({
  credits,
  maxCredits,
  hasCredits,
  planName,
}: {
  credits: number;
  maxCredits: number;
  hasCredits: boolean;
  planName?: string;
}) {
  // Quotas are plan-based (as low as 3/month), so flag "low" proportionally
  // instead of at a fixed count.
  const isLow =
    credits === 0 || (maxCredits > 0 && credits / maxCredits <= 0.2);

  return (
    <div className="p-3 border-t border-border bg-card/80 shrink-0">
      <div className="flex items-center justify-between mb-1.5">
        <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Coins
            className={`w-3.5 h-3.5 ${
              hasCredits ? "text-brand-600" : "text-red-500"
            }`}
          />
          Social searches
        </span>
        <span
          className={`text-xs font-semibold ${
            isLow ? "text-red-500" : "text-muted-foreground"
          }`}
        >
          {credits} / {maxCredits}
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            isLow ? "bg-red-500" : "bg-brand-600"
          }`}
          style={{
            width: `${Math.max(0, maxCredits > 0 ? (credits / maxCredits) * 100 : 0)}%`,
          }}
        />
      </div>
      <p className="text-[10px] text-muted-foreground mt-1.5 leading-snug">
        {planName ? `${planName} plan · ` : ""}Resets monthly · 1 search per
        question
      </p>
    </div>
  );
}

export function ChatHistorySidebar({
  chats,
  activeChatId,
  credits,
  maxCredits,
  hasCredits,
  planName,
  chatsLoading = false,
  creditsLoading = false,
  onNewChat,
  onSelectChat,
  onDeleteChat,
  disabled = false,
  className = "",
}: ChatHistorySidebarProps) {
  return (
    <aside
      className={`flex flex-col border-r border-border bg-muted/80 shrink-0 ${className}`}
    >
      <div className="p-3 border-b border-border">
        <button
          type="button"
          onClick={onNewChat}
          disabled={disabled || chatsLoading}
          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <MessageSquarePlus className="w-4 h-4" />
          New chat
        </button>
      </div>

      <div className="px-3 pt-3 pb-1">
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <History className="w-3.5 h-3.5" />
          History
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-1">
        {chatsLoading ? (
          <ChatListSkeleton />
        ) : chats.length === 0 ? (
          <p className="px-2 py-4 text-xs text-muted-foreground text-center">
            No chats yet
          </p>
        ) : (
          chats.map((chat) => {
            const isActive = chat.id === activeChatId;
            const messageCount = chat._count?.messages ?? 0;

            return (
              <div
                key={chat.id}
                className={`group relative rounded-lg transition-colors ${
                  isActive ? "bg-brand-100" : "hover:bg-card"
                }`}
              >
                <button
                  type="button"
                  onClick={() => onSelectChat(chat.id)}
                  disabled={disabled}
                  className="w-full text-left px-3 py-2.5 pr-9 disabled:opacity-50"
                >
                  <p
                    className={`text-sm font-medium truncate ${
                      isActive ? "text-brand-900" : "text-foreground"
                    }`}
                  >
                    {chat.title}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {formatRelativeTime(chat.updatedAt)}
                    {messageCount > 0
                      ? ` · ${messageCount} question${messageCount === 1 ? "" : "s"}`
                      : ""}
                  </p>
                </button>

                {chats.length > 1 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteChat(chat.id);
                    }}
                    disabled={disabled}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-red-500 hover:bg-red-50 transition-all disabled:opacity-0"
                    title="Delete chat"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {creditsLoading ? (
        <CreditsBarSkeleton />
      ) : (
        <CreditsBar
          credits={credits}
          maxCredits={maxCredits}
          hasCredits={hasCredits}
          planName={planName}
        />
      )}
    </aside>
  );
}
